/**
 * Fase 1b — Public visning av Vedlegg A — Tekniske og organisatoriske sikkerhetstiltak
 *
 * Henter gjeldende versjon fra /vedlegg-a-sikkerhetstiltak.json og renderer
 * markdown-innholdet. Vedlegget er juridisk vedlegg til DPA-1, DPA-2 og DPA-3.
 */
import { useEffect, useState } from 'react';
import { Shield, Download, ExternalLink, Printer } from 'lucide-react';

interface VedleggAResponse {
  source: string;
  template_name: string;
  template_type: string;
  version: string;
  last_updated: string;
  content_markdown: string;
}

/**
 * Minimal markdown → HTML renderer. Håndterer overskrifter, lister,
 * fet/kursiv, tabeller, blockquote. Samme nivå som dpa-pdf-generator.
 */
function mdToHtml(md: string): string {
  let html = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Tabeller
  html = html.replace(/((?:^\|.+\|\s*$\n?)+)/gm, (match) => {
    const lines = match.trim().split('\n');
    if (lines.length < 2) return match;
    const rows = lines.filter((l) => !/^\|[\s\-|:]+\|\s*$/.test(l));
    const [headerLine, ...bodyLines] = rows;
    const parseRow = (line: string) =>
      line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
    const headers = parseRow(headerLine);
    let out = '<table class="va-table"><thead><tr>';
    for (const h of headers) out += `<th>${h}</th>`;
    out += '</tr></thead><tbody>';
    for (const bl of bodyLines) {
      const cells = parseRow(bl);
      out += '<tr>';
      for (const c of cells) out += `<td>${c}</td>`;
      out += '</tr>';
    }
    out += '</tbody></table>';
    return out;
  });

  // Overskrifter
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Horizontal rule
  html = html.replace(/^---+$/gm, '<hr>');

  // Fet/kursiv/kode
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/(?<!\*)\*(?!\*)([^*]+?)\*/g, '<em>$1</em>');
  html = html.replace(/`([^`]+?)`/g, '<code>$1</code>');

  // Blockquotes
  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');
  html = html.replace(/(<\/blockquote>\n<blockquote>)+/g, '<br>');

  // Nummerte lister
  html = html.replace(/(?:^\d+\. .+\n?)+/gm, (m) => {
    const items = m.split('\n').filter(Boolean).map((l) => l.replace(/^\d+\.\s*/, ''));
    return '<ol>' + items.map((i) => `<li>${i}</li>`).join('') + '</ol>';
  });

  // Bindestrek-lister
  html = html.replace(/(?:^- .+\n?)+/gm, (m) => {
    const items = m.split('\n').filter(Boolean).map((l) => l.replace(/^-\s*/, ''));
    return '<ul>' + items.map((i) => `<li>${i}</li>`).join('') + '</ul>';
  });

  // Avsnitt
  const blocks = html.split(/\n\n+/);
  html = blocks
    .map((b) => {
      const trimmed = b.trim();
      if (!trimmed) return '';
      if (/^<(h[1-6]|p|ul|ol|li|blockquote|table|thead|tbody|tr|td|hr|div)/i.test(trimmed)) {
        return trimmed;
      }
      return `<p>${trimmed.replace(/\n/g, '<br>')}</p>`;
    })
    .join('\n');

  return html;
}

export default function VedleggASikkerhetstiltakPage() {
  const [data, setData] = useState<VedleggAResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/vedlegg-a-sikkerhetstiltak.json')
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Laster Vedlegg A...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md bg-white border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-bold text-red-700 mb-2">Kunne ikke laste Vedlegg A</h2>
          <p className="text-sm text-gray-600">{error || 'Ingen data mottatt.'}</p>
        </div>
      </div>
    );
  }

  const html = mdToHtml(data.content_markdown);
  const updated = new Date(data.last_updated).toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        .va-prose h1 { color: #033459; border-bottom: 3px solid #FDB13E; padding-bottom: 0.4rem; font-size: 1.75rem; font-weight: 700; margin-top: 2rem; margin-bottom: 1rem; }
        .va-prose h2 { color: #033459; font-size: 1.4rem; font-weight: 700; margin-top: 2rem; margin-bottom: 0.8rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.3rem; }
        .va-prose h3 { color: #022137; font-size: 1.15rem; font-weight: 600; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .va-prose h4 { color: #333; font-size: 1rem; font-weight: 600; margin-top: 1rem; margin-bottom: 0.4rem; }
        .va-prose p { margin: 0.75rem 0; line-height: 1.65; color: #1f2937; }
        .va-prose strong { color: #033459; }
        .va-prose ul, .va-prose ol { padding-left: 1.5rem; margin: 0.6rem 0; }
        .va-prose li { margin: 0.3rem 0; line-height: 1.5; }
        .va-prose hr { border: none; border-top: 1px solid #e5e7eb; margin: 1.5rem 0; }
        .va-prose code { background: #f4f6fa; padding: 0.1rem 0.3rem; border-radius: 0.15rem; font-size: 0.9em; }
        .va-prose blockquote { border-left: 3px solid #FDB13E; background: #fffaf0; padding: 0.5rem 1rem; margin: 1rem 0; color: #78350f; }
        .va-table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
        .va-table th, .va-table td { border: 1px solid #d1d5db; padding: 0.5rem 0.75rem; text-align: left; vertical-align: top; }
        .va-table th { background: #f4f6fa; color: #033459; font-weight: 600; }
        @media print {
          .va-no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="va-no-print mb-6">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="w-10 h-10 text-kf-primary flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-3xl font-bold text-kf-primary mb-1">Vedlegg A</h1>
              <p className="text-gray-600">
                Tekniske og organisatoriske sikkerhetstiltak — felles vedlegg til databehandleravtalene
                DPA-1, DPA-2 og DPA-3.
              </p>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4 flex flex-wrap items-center gap-4 justify-between">
            <div className="text-sm text-gray-700">
              <div>
                <strong>Gjeldende versjon:</strong> {data.version}
              </div>
              <div className="text-xs text-gray-500">Sist oppdatert: {updated}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-kf-primary text-white rounded hover:bg-kf-primary/90"
              >
                <Printer size={16} />
                Skriv ut / Lagre som PDF
              </button>
              <a
                href="/vedlegg-a-sikkerhetstiltak.json"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-kf-primary text-kf-primary rounded hover:bg-kf-primary/5"
              >
                <Download size={16} />
                JSON
              </a>
              <a
                href="/underdatabehandlere"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                <ExternalLink size={16} />
                Underdatabehandlere
              </a>
            </div>
          </div>
        </div>

        {/* Innhold */}
        <article className="va-prose bg-white rounded-lg shadow-sm border p-6 md:p-10">
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </article>

        {/* Footer */}
        <div className="va-no-print mt-6 text-center text-xs text-gray-500">
          Kompetanseforbundet · Org. nr. 926 920 960 ·{' '}
          <a href="https://studieplan.kompetanseattest.no" className="text-kf-primary hover:underline">
            studieplan.kompetanseattest.no
          </a>
        </div>
      </div>
    </div>
  );
}
